import React from 'react';
import { motion } from 'framer-motion';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Search, X, Filter } from 'lucide-react';
import { industryTrainings } from '../../data/industries/industryTrainingsData';

export const TrainingFilters = ({
  searchQuery,
  setSearchQuery,
  industryFilter,
  setIndustryFilter,
  levelFilter,
  setLevelFilter,
  modeFilter,
  setModeFilter,
  totalResults,
}) => {
  const hasActiveFilters = searchQuery || industryFilter || levelFilter || modeFilter;

  const clearAllFilters = () => {
    setSearchQuery('');
    setIndustryFilter('');
    setLevelFilter('');
    setModeFilter('');
  };

  const levelOptions = [
    { value: 'all', label: 'All Levels' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'foundation', label: 'Foundation' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'awareness', label: 'Awareness to Lead Auditor' },
  ];

  const modeOptions = [
    { value: 'all', label: 'All Modes' },
    { value: 'Classroom', label: 'Classroom' },
    { value: 'Online', label: 'Online' },
    { value: 'Hybrid', label: 'Hybrid' },
    { value: 'On-Site', label: 'On-Site' },
    { value: 'Corporate', label: 'Corporate' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8"
    >
      {/* Search and Filter Header */}
      <div className="flex items-center gap-3 mb-6">
        <Filter className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Find Your Training</h3>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="ml-auto text-gray-500 hover:text-red-500"
          >
            <X className="h-4 w-4 mr-1" />
            Clear Filters
          </Button>
        )}
      </div>

      {/* Search Bar */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <Input
          type="text"
          placeholder="Search trainings by name, topic, or keyword..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-12 h-12 text-base border-gray-200 focus:border-blue-500 rounded-xl"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Filter Dropdowns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Industry Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Industry
          </label>
          <Select value={industryFilter} onValueChange={setIndustryFilter}>
            <SelectTrigger className="h-11 rounded-lg border-gray-200">
              <SelectValue placeholder="All Industries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Industries</SelectItem>
              {industryTrainings.map((industry) => (
                <SelectItem key={industry.slug} value={industry.slug}>
                  {industry.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Level Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Level
          </label>
          <Select value={levelFilter} onValueChange={setLevelFilter}>
            <SelectTrigger className="h-11 rounded-lg border-gray-200">
              <SelectValue placeholder="All Levels" />
            </SelectTrigger>
            <SelectContent>
              {levelOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Delivery Mode Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Delivery Mode
          </label>
          <Select value={modeFilter} onValueChange={setModeFilter}>
            <SelectTrigger className="h-11 rounded-lg border-gray-200">
              <SelectValue placeholder="All Modes" />
            </SelectTrigger>
            <SelectContent>
              {modeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Results Count */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <p className="text-sm text-gray-600">
          Showing <span className="font-semibold text-blue-600">{totalResults}</span> training program{totalResults !== 1 ? 's' : ''}
          {hasActiveFilters && ' matching your criteria'}
        </p>
      </div>
    </motion.div>
  );
};
